import { useMutation, useQueryClient } from "@tanstack/react-query";
import { instance } from "@semo/api-contracts";

/**
 * 견적 철회 (V-13) — `POST /quote-responses/:id/withdraw`.
 *
 * ⚠️ Authorization 헤더를 직접 만들지 않는다 — 임베드 로그인은 토큰을 axios 인스턴스 기본 헤더에만
 * 심고 localStorage 에는 쓰지 않는다(`useMyCompanyProfile` 주석 참고). `instance` 를 그대로 쓴다.
 *
 * 생성 클라이언트(orval)를 다시 만들지 않으려고 직접 부른다. 경로 모양은 생성 훅과 같다(`/api/v1` 없이).
 */

export interface WithdrawQuoteResponseResult {
  quoteResponseId: string;
  withdrawnAt: string;
  withdrawalReason: string | null;
}

export interface WithdrawQuoteResponseVariables {
  quoteResponseId: string;
  /** 선택. 앞뒤 공백은 서버가 걷어 낸다. */
  reason?: string;
}

/** 백엔드는 전 응답을 `{ success, data }` 로 감싼다(ResponseValidationInterceptor). */
interface Envelope<T> {
  data: T;
}

const FALLBACK_ERROR_MESSAGE =
  "견적을 철회하지 못했습니다. 잠시 후 다시 시도해 주세요.";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

/**
 * 서버가 보낸 한국어 문구를 그대로 꺼낸다(«이미 철회한 견적입니다.» 등).
 * 문구가 없으면(네트워크 오류 등) 기본 안내.
 */
export function getWithdrawErrorMessage(error: unknown): string {
  if (!isRecord(error) || !isRecord(error.response)) {
    return FALLBACK_ERROR_MESSAGE;
  }
  const data = error.response.data;
  if (!isRecord(data)) return FALLBACK_ERROR_MESSAGE;
  const nested = isRecord(data.error)
    ? nonEmptyString(data.error.message)
    : null;
  return nested ?? nonEmptyString(data.message) ?? FALLBACK_ERROR_MESSAGE;
}

/** 철회 뒤 다시 읽어야 하는 목록·상세 — 공고 상세(경로 인자가 공고번호일 수도 있다)·참여 목록·채팅. */
const INVALIDATE_PREFIXES = [
  "/external/quote-requests",
  "/quote-requests",
  "/quote-responses",
  "/chat/rooms",
];

export function useWithdrawQuoteResponse() {
  const queryClient = useQueryClient();

  return useMutation<
    WithdrawQuoteResponseResult,
    unknown,
    WithdrawQuoteResponseVariables
  >({
    mutationFn: async ({ quoteResponseId, reason }) => {
      const trimmed = reason?.trim();
      const response = await instance.post<
        Envelope<WithdrawQuoteResponseResult>
      >(
        `/quote-responses/${encodeURIComponent(quoteResponseId)}/withdraw`,
        trimmed ? { reason: trimmed } : {},
      );
      return response.data.data;
    },
    // 다시 읽기를 기다리지 않는다 — 성공 안내가 먼저 뜨고, 화면은 새 값이 오면 «철회됨» 으로 바뀐다.
    onSuccess: () => {
      void queryClient.invalidateQueries({
        predicate: (query) => {
          const head = query.queryKey[0];
          return (
            typeof head === "string" &&
            INVALIDATE_PREFIXES.some((prefix) => head.startsWith(prefix))
          );
        },
      });
    },
  });
}
